/**
 * lastKnown — Jun 2026 · Instant Dashboard.
 *
 * Keeps the last successful payload for a screen in localStorage so the next
 * visit paints immediately instead of flashing a skeleton, then refreshes in
 * the background and swaps the fresh data in.
 *
 * Snapshots are wiped on logout / identity swap (see AuthContext) so one
 * account never sees another's numbers, and anything older than a day is
 * discarded — a stale-by-a-week dashboard is worse than a short wait.
 */
const PREFIX = "tlb:snapshot:";
// Deliberately outside PREFIX so `clearSnapshots()` doesn't wipe it.
const OWNER_KEY = "tlb:snapshot-owner:v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
// A plan list with every nested payload gets big; past ~2MB we skip the
// snapshot rather than risk filling the origin's storage quota.
const MAX_BYTES = 2 * 1024 * 1024;

function readOwner() {
  try {
    return localStorage.getItem(OWNER_KEY) || "anon";
  } catch {
    return "anon";
  }
}

// Keys are namespaced by account, so even a snapshot that outlives a session
// (browser closed and reopened, OAuth full-page return) can't be read by the
// next person to sign in on this browser.
function storageKey(key) {
  return `${PREFIX}${readOwner()}:${key}`;
}

/**
 * Records who these snapshots belong to. Called whenever an identity is
 * established (see AuthContext); a different account than last time wipes
 * everything, which is the protection a reload-based session swap needs —
 * in-memory identity tracking is reset by the reload.
 */
export function setSnapshotOwner(identity) {
  const next = identity || "anon";
  try {
    if (localStorage.getItem(OWNER_KEY) === next) return;
    clearSnapshots();
    localStorage.setItem(OWNER_KEY, next);
  } catch {
    /* ignore */
  }
}

export function readSnapshot(key) {
  const k = storageKey(key);
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > MAX_AGE_MS) {
      localStorage.removeItem(k);
      return null;
    }
    return { data: parsed.data, at: parsed.at };
  } catch {
    return null;
  }
}

export function writeSnapshot(key, data) {
  const k = storageKey(key);
  try {
    const raw = JSON.stringify({ at: Date.now(), data });
    if (raw.length > MAX_BYTES) {
      localStorage.removeItem(k);
      return;
    }
    localStorage.setItem(k, raw);
  } catch {
    /* quota / private mode — the screen just loads normally next time */
  }
}

export function clearSnapshots() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

// "just now" / "4 min ago" / "2 hr ago" — deliberately coarse.
export function freshnessLabel(at, now = Date.now()) {
  if (!at) return null;
  const mins = Math.floor((now - at) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
